import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextareaAutosize,
  Stack,
  Divider,
  Paper,
  IconButton,
  Breadcrumbs,
  Link as MuiLink,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Avatar,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  ShoppingCart,
  Business,
  Person,
  Email,
  Phone,
  CalendarToday,
  Description,
  CheckCircle,
  AttachMoney,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { productDisplayApi } from '../api/productDisplayApi';
import { useGetProductByIdQuery } from '../api/productDisplayApi';

interface QuoteRequestProps {}

export const QuoteRequest: React.FC<QuoteRequestProps> = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Product Information
    productId: searchParams.get('productId') || '',
    quantity: 1,
    deliveryDate: '',
    specialRequirements: '',
    
    // Contact Information
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    companyName: '',
    companySize: '',
    
    // Project Details
    projectType: '',
    urgency: 'normal',
    budget: '',
    timeline: '',
    
    // Additional Information
    additionalNotes: '',
    preferredContactMethod: 'email',
  });

  const { data: selectedProduct, isLoading: productLoading } = useGetProductByIdQuery(formData.productId || '', { skip: !formData.productId });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // TODO: Implement actual API call
      // await quoteApi.submitQuoteRequest(formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSuccess(true);
      setActiveStep(3); // Success step
    } catch (err) {
      setError('Failed to submit quote request. Please try again.');
      console.error('Error submitting quote:', err);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      label: 'Product Selection',
      description: 'Choose the product you need a quote for',
      icon: <ShoppingCart />,
    },
    {
      label: 'Contact Information',
      description: 'Provide your contact details',
      icon: <Person />,
    },
    {
      label: 'Project Details',
      description: 'Tell us about your project requirements',
      icon: <Business />,
    },
    {
      label: 'Review & Submit',
      description: 'Review your quote request and submit',
      icon: <Send />,
    },
  ];

  const renderProductSelection = () => (
    <Box>
      {productLoading ? (
        <CircularProgress sx={{ mt: 4 }} />
      ) : selectedProduct ? (
        <Card sx={{ mb: 3, background: 'rgba(255, 255, 255, 0.9)' }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {selectedProduct.imageUrl ? (
                    <img
                      src={selectedProduct.imageUrl}
                      alt={selectedProduct.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography variant="h6" color="text.secondary">
                      Product Image
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {selectedProduct.name}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedProduct.description}
                </Typography>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                  ${selectedProduct.price}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          No product selected. Please select a product from our catalog.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Quantity"
            type="number"
            value={formData.quantity}
            onChange={(e) => handleInputChange('quantity', parseInt(e.target.value))}
            inputProps={{ min: 1 }}
            helperText="Minimum order quantity: 1"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Required Delivery Date"
            type="date"
            value={formData.deliveryDate}
            onChange={(e) => handleInputChange('deliveryDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="When do you need the product?"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Special Requirements"
            multiline
            rows={3}
            value={formData.specialRequirements}
            onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
            helperText="Any specific requirements or modifications needed?"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderContactInformation = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Full Name"
          value={formData.contactName}
          onChange={(e) => handleInputChange('contactName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={formData.contactEmail}
          onChange={(e) => handleInputChange('contactEmail', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.contactPhone}
          onChange={(e) => handleInputChange('contactPhone', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Company Name"
          value={formData.companyName}
          onChange={(e) => handleInputChange('companyName', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Company Size</InputLabel>
          <Select
            value={formData.companySize}
            onChange={(e) => handleInputChange('companySize', e.target.value)}
            label="Company Size"
          >
            <MenuItem value="startup">Startup (1-10 employees)</MenuItem>
            <MenuItem value="small">Small Business (11-50 employees)</MenuItem>
            <MenuItem value="medium">Medium Business (51-200 employees)</MenuItem>
            <MenuItem value="large">Large Business (200+ employees)</MenuItem>
            <MenuItem value="enterprise">Enterprise</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Preferred Contact Method</InputLabel>
          <Select
            value={formData.preferredContactMethod}
            onChange={(e) => handleInputChange('preferredContactMethod', e.target.value)}
            label="Preferred Contact Method"
          >
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="phone">Phone</MenuItem>
            <MenuItem value="both">Both</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderProjectDetails = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Project Type</InputLabel>
          <Select
            value={formData.projectType}
            onChange={(e) => handleInputChange('projectType', e.target.value)}
            label="Project Type"
          >
            <MenuItem value="new">New Project</MenuItem>
            <MenuItem value="replacement">Replacement</MenuItem>
            <MenuItem value="upgrade">Upgrade</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Urgency</InputLabel>
          <Select
            value={formData.urgency}
            onChange={(e) => handleInputChange('urgency', e.target.value)}
            label="Urgency"
          >
            <MenuItem value="low">Low Priority</MenuItem>
            <MenuItem value="normal">Normal Priority</MenuItem>
            <MenuItem value="high">High Priority</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Budget Range"
          value={formData.budget}
          onChange={(e) => handleInputChange('budget', e.target.value)}
          placeholder="e.g., $10,000 - $50,000"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Project Timeline"
          value={formData.timeline}
          onChange={(e) => handleInputChange('timeline', e.target.value)}
          placeholder="e.g., 3-6 months"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Additional Notes"
          multiline
          rows={4}
          value={formData.additionalNotes}
          onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
          helperText="Any additional information about your project requirements"
        />
      </Grid>
    </Grid>
  );

  const renderReview = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Review Your Quote Request
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, background: 'rgba(255, 255, 255, 0.9)' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Product Information
            </Typography>
            <Stack spacing={1}>
              <Box>
                <Typography variant="body2" color="text.secondary">Product</Typography>
                <Typography variant="body1">{selectedProduct?.name || 'Not selected'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Quantity</Typography>
                <Typography variant="body1">{formData.quantity}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Delivery Date</Typography>
                <Typography variant="body1">{formData.deliveryDate || 'Not specified'}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, background: 'rgba(255, 255, 255, 0.9)' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Contact Information
            </Typography>
            <Stack spacing={1}>
              <Box>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1">{formData.contactName}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{formData.contactEmail}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{formData.contactPhone}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSuccess = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Quote Request Submitted!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Thank you for your quote request. Our team will review your requirements and get back to you within 24-48 hours.
      </Typography>
      <Stack direction="row" spacing={2} justifyContent="center">
        <Button
          variant="contained"
          onClick={() => navigate('/products')}
          sx={{
            background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
            },
          }}
        >
          Browse More Products
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/contact')}
        >
          Contact Support
        </Button>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3, color: 'white' }}>
          <MuiLink color="inherit" href="/" underline="hover">
            Home
          </MuiLink>
          <MuiLink color="inherit" href="/products" underline="hover">
            Products
          </MuiLink>
          <Typography color="inherit">Request Quote</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Request a Quote
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tell us about your project requirements and we'll provide you with a detailed quote
          </Typography>
        </Paper>

        {/* Stepper */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar sx={{ bgcolor: activeStep >= index ? 'primary.main' : 'grey.300' }}>
                      {step.icon}
                    </Avatar>
                  )}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mt: 2 }}>
                    {index === 0 && renderProductSelection()}
                    {index === 1 && renderContactInformation()}
                    {index === 2 && renderProjectDetails()}
                    {index === 3 && renderReview()}
                    
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        onClick={index === steps.length - 1 ? handleSubmit : handleNext}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : (index === steps.length - 1 ? <Send /> : undefined)}
                        sx={{
                          mr: 1,
                          background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                          },
                        }}
                      >
                        {loading ? 'Submitting...' : (index === steps.length - 1 ? 'Submit Quote Request' : 'Continue')}
                      </Button>
                      <Button
                        disabled={index === 0}
                        onClick={handleBack}
                        sx={{ mr: 1 }}
                      >
                        Back
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Success Message */}
        {success && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mt: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            {renderSuccess()}
          </Paper>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Container>
    </Box>
  );
}; 